// Medicine Database Manager — search / add / edit / delete across both
// drug sources: "My List" (my_drug_list, 27k+ with pack & price) and the
// curated "Old DB" (medicines). Free-typed Rx medicines auto-land in My List
// (source=auto_captured) and can be polished here.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, Pencil, Trash2, Package, AlertCircle } from "lucide-react";
import { CLINIC, LogoTile } from "../lib/clinic";
import { CorrectionQueue } from "../components/CorrectionQueue"; // MT-11B.8 read-only

const ROUTES = ["Oral", "IV", "IM", "SC", "Inhalation", "Topical", "Sublingual"];
const emptyForm = () => ({
  brand_name: "", generic_name: "", composition: "", strength: "", dosage_form: "",
  manufacturer: "", route: "Oral", standard_dose: "", frequency: "", timing: "",
  instructions: "", pack: "", price: "",
});

const SOURCE_BADGE = {
  auto_captured: { label: "AUTO", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  manual: { label: "ADDED", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  quickfill: { label: "QUICK", cls: "bg-sky-50 text-sky-700 border-sky-300" },
};
// MT-11B.7: auto-captured rows stay out of the Rx typeahead until a db-editor
// reviews (saves) them here.
const PENDING_BADGE = { label: "PENDING REVIEW", cls: "bg-rose-50 text-rose-700 border-rose-300" };

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

export default function MedicineManager() {
  const [source, setSource] = useState("mylist");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null); // medicine_id when editing
  const [dest, setDest] = useState("mylist");
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const t = useRef();

  const fetchRows = async (query, src) => {
    if (src === "queue") return;
    setLoading(true);
    try {
      if (query.trim().length >= 2) {
        const ep = src === "mylist" ? "/mydrugs/search" : "/medicines/search";
        const r = await api.get(`${ep}?q=${encodeURIComponent(query.trim())}&page_size=50`);
        setRows(r.data.results || []);
        setTotal(r.data.total || 0);
        setMode("search");
      } else {
        const ep = src === "mylist" ? "/mydrugs/recent" : "/medicines/recent";
        const r = await api.get(`${ep}?limit=30`);
        setRows(r.data.results || []);
        setTotal((r.data.results || []).length);
        setMode("recent");
      }
    } catch (e) {
      console.debug("[silent]", e);
      toast.error("Failed to load medicines");
    }
    setLoading(false);
  };

  useEffect(() => { fetchRows(q, source); /* eslint-disable-next-line */ }, [source]);

  const onSearch = (val) => {
    setQ(val);
    clearTimeout(t.current);
    t.current = setTimeout(() => fetchRows(val, source), 350);
  };

  const openAdd = () => {
    setEditing(null);
    setDest(source);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.medicine_id);
    setDest(source);
    setForm({
      brand_name: row.brand_name || "", generic_name: row.generic_name || "",
      composition: row.composition || "", strength: row.strength || "",
      dosage_form: row.dosage_form || "", manufacturer: row.manufacturer || "",
      route: row.route || "Oral", standard_dose: row.standard_dose || "",
      frequency: row.frequency || "", timing: row.timing || "",
      instructions: row.instructions || "", pack: row.pack || "",
      price: row.price ?? "",
    });
    setDialogOpen(true);
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.brand_name.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      const isMy = dest === "mylist";
      const payload = { ...form };
      if (!isMy) { delete payload.pack; delete payload.price; }
      if (editing) {
        await api.put(`${isMy ? "/mydrugs" : "/medicines"}/${editing}`, payload);
        toast.success(`Updated "${form.brand_name}"`);
      } else {
        await api.post(isMy ? "/mydrugs" : "/medicines", payload);
        toast.success(`Added "${form.brand_name}" to ${isMy ? "My List" : "Old DB"}`);
        if (dest !== source) setSource(dest);
      }
      setDialogOpen(false);
      fetchRows(q, dest === source ? source : dest);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const doDelete = async (row) => {
    try {
      await api.delete(`${source === "mylist" ? "/mydrugs" : "/medicines"}/${row.medicine_id}`);
      toast.success(`Deleted "${row.brand_name}"`);
      setPendingDelete(null);
      fetchRows(q, source);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const isMyTab = source === "mylist";
  const isQueue = source === "queue";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="med-manager-page">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-1.5 rounded hover:bg-slate-100" data-testid="back-to-dashboard"><ArrowLeft size={16} /></Link>
          <LogoTile />
          <div>
            <div className="text-sm font-heading font-semibold text-slate-900 flex items-center gap-1.5"><Package size={14} /> Medicine Database</div>
            <div className="text-[11px] text-slate-500">{CLINIC.name} · {CLINIC.doctorName}</div>
          </div>
        </div>
        <Button size="sm" onClick={openAdd} className="bg-pink-600 hover:bg-pink-700 text-white gap-1" data-testid="med-add-btn">
          <Plus size={12} /> Add medicine
        </Button>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-900 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Auto-capture is ON:</strong> any medicine you type free-hand on a prescription (not picked from the dropdown) is added to <strong>My List</strong> automatically when you save the visit — tagged <span className="font-semibold text-amber-700">AUTO</span> below so you can polish composition, pack &amp; price here.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs font-semibold">
            <button
              onClick={() => { setPendingDelete(null); setSource("mylist"); }}
              className={`px-3 py-1.5 ${isMyTab ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="med-src-mylist"
            >My List</button>
            <button
              onClick={() => { setPendingDelete(null); setSource("olddb"); }}
              className={`px-3 py-1.5 ${source === "olddb" ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="med-src-olddb"
            >Old DB</button>
            <button
              onClick={() => { setPendingDelete(null); setSource("queue"); }}
              className={`px-3 py-1.5 ${isQueue ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="med-src-queue"
              title="Read-only: per-prescription corrections grouped by source record"
            >Correction queue</button>
          </div>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={isMyTab ? "Search My List (brand / generic / composition)…" : "Search Old DB (brand / generic / manufacturer)…"}
              className="h-8 pl-8 text-sm"
              data-testid="med-search-input"
            />
          </div>
          <div className="text-[11px] text-slate-500" data-testid="med-count-line">
            {mode === "search" ? `${total} match${total === 1 ? "" : "es"}` : "Recently added / captured"}
          </div>
        </div>

        {isQueue && <CorrectionQueue />}
        {!isQueue && <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="p-3 min-w-[260px]">Brand · Composition</th>
                <th className="p-3 min-w-[110px]">Strength / Form</th>
                <th className="p-3 min-w-[140px]">Manufacturer</th>
                {isMyTab && <th className="p-3 min-w-[130px]">Pack · Price</th>}
                <th className="p-3 min-w-[150px]">Rx defaults</th>
                <th className="p-3 w-16">Source</th>
                <th className="p-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500" data-testid="med-empty-state">
                  {mode === "recent"
                    ? "No custom additions yet — auto-captured and manually added medicines will appear here. Use the search box to browse the full database."
                    : "No medicines match your search."}
                </td></tr>
              )}
              {!loading && rows.map((r, i) => {
                const badge = SOURCE_BADGE[r.source];
                return (
                  <tr key={r.medicine_id || i} className="border-t border-slate-100 align-top hover:bg-slate-50/60" data-testid={`med-row-${i}`}>
                    <td className="p-3">
                      <div className="font-semibold text-slate-900">{r.brand_name}</div>
                      {r.generic_name && <div className="text-slate-500">{r.generic_name}</div>}
                      {r.composition && r.composition !== r.generic_name && (
                        <div className="text-[10px] text-slate-400 leading-tight">{r.composition}</div>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {r.strength && <div>{r.strength}</div>}
                      {r.dosage_form && <div className="text-[10px] text-slate-400">{r.dosage_form}</div>}
                    </td>
                    <td className="p-3 text-slate-600">{r.manufacturer || "—"}</td>
                    {isMyTab && (
                      <td className="p-3 text-slate-600">
                        {r.pack || r.price != null ? (
                          <>
                            {r.pack && <div className="text-[10px]">{r.pack}</div>}
                            {r.price != null && <div className="font-semibold text-emerald-700">₹{r.price}</div>}
                          </>
                        ) : "—"}
                      </td>
                    )}
                    <td className="p-3 text-slate-600">
                      {[r.standard_dose, r.frequency, r.timing].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="p-3">
                      {badge && (
                        <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded border ${badge.cls}`} data-testid={`med-source-badge-${i}`}>
                          {badge.label}
                        </span>
                      )}
                      {r.review_status === "pending_review" && (
                        <span className={`ml-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded border ${PENDING_BADGE.cls}`} data-testid={`med-pending-badge-${i}`} title="Hidden from the Rx typeahead until reviewed — edit & save to verify">
                          {PENDING_BADGE.label}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit" data-testid={`med-edit-${i}`}>
                          <Pencil size={13} />
                        </button>
                        {pendingDelete === r.medicine_id ? (
                          <button onClick={() => doDelete(r)} className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-semibold" data-testid={`med-delete-confirm-${i}`}>
                            Confirm?
                          </button>
                        ) : (
                          <button onClick={() => setPendingDelete(r.medicine_id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`med-delete-${i}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="med-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit medicine" : "Add medicine"}</DialogTitle>
            <DialogDescription>
              {editing
                ? `Editing "${form.brand_name}" in ${dest === "mylist" ? "My List" : "the Old DB"}.`
                : "New entry becomes instantly searchable in the prescription dropdown."}
            </DialogDescription>
          </DialogHeader>

          {!editing && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Save to</span>
              <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs font-semibold">
                <button onClick={() => setDest("mylist")} className={`px-3 py-1 ${dest === "mylist" ? "bg-pink-600 text-white" : "bg-white text-slate-600"}`} data-testid="med-dest-mylist">My List</button>
                <button onClick={() => setDest("olddb")} className={`px-3 py-1 ${dest === "olddb" ? "bg-pink-600 text-white" : "bg-white text-slate-600"}`} data-testid="med-dest-olddb">Old DB</button>
              </div>
              {dest === "mylist" && <span className="text-[10px] text-slate-400">supports pack &amp; price</span>}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Brand name *">
              <Input value={form.brand_name} onChange={(e) => upd("brand_name", e.target.value)} placeholder="e.g. Dolo 650" className="h-8 text-sm" data-testid="med-f-brand" />
            </Field>
            <Field label="Generic name">
              <Input value={form.generic_name} onChange={(e) => upd("generic_name", e.target.value)} placeholder="e.g. Paracetamol" className="h-8 text-sm" data-testid="med-f-generic" />
            </Field>
            <Field label="Composition">
              <Input value={form.composition} onChange={(e) => upd("composition", e.target.value)} placeholder="e.g. Paracetamol (650mg)" className="h-8 text-sm" data-testid="med-f-composition" />
            </Field>
            <Field label="Strength">
              <Input value={form.strength} onChange={(e) => upd("strength", e.target.value)} placeholder="e.g. 650mg" className="h-8 text-sm" data-testid="med-f-strength" />
            </Field>
            <Field label="Dosage form">
              <Input value={form.dosage_form} onChange={(e) => upd("dosage_form", e.target.value)} placeholder="Tablet / Syrup / Injection" className="h-8 text-sm" data-testid="med-f-form" />
            </Field>
            <Field label="Manufacturer">
              <Input value={form.manufacturer} onChange={(e) => upd("manufacturer", e.target.value)} placeholder="e.g. Micro Labs" className="h-8 text-sm" data-testid="med-f-manufacturer" />
            </Field>
            <Field label="Route">
              <select value={form.route} onChange={(e) => upd("route", e.target.value)} className="h-8 w-full text-sm border border-slate-200 rounded px-2" data-testid="med-f-route">
                {ROUTES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Default dose">
              <Input value={form.standard_dose} onChange={(e) => upd("standard_dose", e.target.value)} placeholder="e.g. 1 tab" className="h-8 text-sm" data-testid="med-f-dose" />
            </Field>
            <Field label="Default frequency">
              <Input value={form.frequency} onChange={(e) => upd("frequency", e.target.value)} placeholder="e.g. BD" className="h-8 text-sm" data-testid="med-f-frequency" />
            </Field>
            <Field label="Default timing">
              <Input value={form.timing} onChange={(e) => upd("timing", e.target.value)} placeholder="e.g. After food" className="h-8 text-sm" data-testid="med-f-timing" />
            </Field>
            {dest === "mylist" && (
              <>
                <Field label="Pack">
                  <Input value={form.pack} onChange={(e) => upd("pack", e.target.value)} placeholder="e.g. strip of 15 tablets" className="h-8 text-sm" data-testid="med-f-pack" />
                </Field>
                <Field label="Price (₹)">
                  <Input value={form.price} onChange={(e) => upd("price", e.target.value)} placeholder="e.g. 33.60" inputMode="decimal" className="h-8 text-sm" data-testid="med-f-price" />
                </Field>
              </>
            )}
            <div className="sm:col-span-2">
              <Field label="Default instructions (printed on Rx)">
                <Input value={form.instructions} onChange={(e) => upd("instructions", e.target.value)} placeholder="e.g. दिवसातून दोन वेळा, जेवणानंतर" className="h-8 text-sm" data-testid="med-f-instructions" />
              </Field>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)} data-testid="med-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="med-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Add medicine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
